import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { TrendUp, ArrowRight } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { PROGRAMS } from "@/data/programs";
import { Container, Section, Overline } from "@/lib/ui";
import { FadeUp } from "@/lib/motion";

// Show a live "most-recommended by peers" ranking based on quiz completions.
// Hidden entirely when there is not enough signal yet.
const MIN_COMPLETIONS = 3;

export const TopPrograms = () => {
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/top-programs?days=90`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { /* silent */ })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  if (!ready) return null;
  if (!data || (data.quiz_completions || 0) < MIN_COMPLETIONS) return null;

  const byCode = Object.fromEntries(PROGRAMS.map((p) => [p.code.toUpperCase(), p]));
  const rows = (data.programs || [])
    .map((r) => ({ ...r, program: byCode[r.code] }))
    .filter((r) => r.program)
    .slice(0, 5);

  if (!rows.length) return null;

  const max = rows[0].count || 1;

  return (
    <Section data-testid="top-programs-section" className="bg-white">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] items-start">
          <FadeUp>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
              <TrendUp size={13} weight="bold" className="text-zoc-teal" />
              Live from the Fit Quiz
            </div>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl font-bold text-[#0B2A4E]">
              What peers are choosing <span className="zoc-gradient-text">right now.</span>
            </h2>
            <p className="mt-4 max-w-md text-slate-600 text-lg leading-relaxed">
              Based on the last 90 days of anonymous quiz results, here are the programs practices are picking most often for their Medicare panels.
            </p>
            <p className="mt-6 text-xs font-semibold text-slate-400">
              {data.quiz_completions} quiz completions · aggregated, no PHI
            </p>
            <Link
              to="/fit"
              data-testid="top-programs-cta"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#0B2A4E] shadow-sm transition-all hover:border-zoc-teal hover:-translate-y-0.5"
            >
              Take the 60-second quiz <ArrowRight size={14} weight="bold" />
            </Link>
          </FadeUp>

          <div className="space-y-3">
            {rows.map((r, i) => {
              const pct = Math.max(6, Math.round((r.count / max) * 100));
              return (
                <motion.div
                  key={r.code}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.06, type: "spring", stiffness: 100, damping: 20 }}
                  data-testid={`top-program-${r.code.toLowerCase()}`}
                >
                  <Link
                    to={`/programs/${r.program.slug}`}
                    className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                  >
                    <span
                      className="grid place-items-center h-11 w-11 rounded-xl text-white text-sm font-display font-extrabold shrink-0"
                      style={{ backgroundColor: r.program.color }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-4">
                        <div>
                          <p className="font-display text-lg font-bold text-[#0B2A4E]">{r.program.code}</p>
                          <p className="text-sm text-slate-500 leading-tight">{r.program.name}</p>
                        </div>
                        <p className="text-xs font-semibold text-slate-500 shrink-0">
                          {Math.round((r.share || 0) * 100)}% of picks
                        </p>
                      </div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${pct}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: 0.1 + i * 0.05 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: r.program.color }}
                        />
                      </div>
                    </div>
                    <ArrowRight size={16} weight="bold" className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-[#0B2A4E]" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default TopPrograms;
